<!-- FinancialPage.vue -->
<template>
  <PageTitle pageTitle="Financeiro" mainTitle="Financeiro" />

  <FinancialFilters v-if="user.type == UserType.ADMIN" />

  <v-card class="mb-25 pingo-card">
    <div class="v-card-header d-flex">
      <div>
        <h5 class="mb-2">Financeiro</h5>
        <p>Atualizado por último em {{ new Date().toLocaleString() }}</p>
      </div>

      <div class="d-flex ga-2">
        <v-dialog
          v-model="isProcessInvoicesManualModalOpen"
          max-width="480"
          scrollable
        >
          <template v-slot:activator="{ props: activatorProps }">
            <button
              v-if="user.type == UserType.ADMIN"
              class="default-btn pingo small"
              v-bind="activatorProps"
            >
              <i class="ri-restart-line"></i>
              Processar todas as faturas
            </button>
          </template>

          <template v-slot:default="{ isActive }">
            <v-form @submit.prevent="handleProcessInvoices()">
              <v-card class="py-0" title="Processar faturas manualmente">
                <v-divider></v-divider>

                <v-card-text class="px-4">
                  Tem certeza que deseja realizar o processamento das faturas
                  manualmente?
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                  <v-btn
                    class="text-capitalize"
                    @click="isActive.value = false"
                  >
                    Fechar
                  </v-btn>

                  <v-spacer></v-spacer>

                  <v-btn
                    color="red-darken-3"
                    variant="tonal"
                    type="submit"
                    style="text-transform: none"
                  >
                    Sim, tenho certeza
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-form>
          </template>
        </v-dialog>

        <v-dialog
          v-model="isProcessInvoicesIndividualModalOpen"
          max-width="480"
          scrollable
        >
          <template v-slot:activator="{ props: activatorProps }">
            <button
              v-if="user.type == UserType.ADMIN"
              class="default-btn pingo small"
              v-bind="activatorProps"
            >
              <i class="ri-user-line"></i>
              Gerar faturamento individual
            </button>
          </template>

          <template v-slot:default="{ isActive }">
            <v-form @submit.prevent="handleSubmit()">
              <v-card class="py-0" title="Processar faturas manualmente">
                <v-divider></v-divider>

                <v-card-text class="px-4">
                  Tem certeza que deseja realizar o processamento das faturas
                  para o usuário selecionado?
                </v-card-text>

                <v-card-text class="px-4">
                  <div class="pingo-form-group without-icon mb-0">
                    <AutoCompleteBusinesses
                      v-model="createInvoiceForm.userId"
                      @update:value="(value: UserResponse) => (updateUserId(value))"
                      :required="true"
                      :return-object="false"
                      :error-messages="
                        v$2.userId.$errors
                          ? v$2.userId.$errors[0]?.$message
                          : undefined
                      "
                    />
                  </div>

                  <div class="pingo-form-group without-icon">
                    <TextInput
                      :type="'date'"
                      :label="'Data Inicial'"
                      v-model="createInvoiceForm.dateStart"
                      :required="false"
                      @update:value="
                        (initial_datetime: string) => updateInitDate(initial_datetime)
                      "
                    />
                  </div>

                  <div class="pingo-form-group without-icon">
                    <TextInput
                      :type="'date'"
                      :label="'Data Final'"
                      :required="false"
                      v-model="createInvoiceForm.dateEnd"
                      @update:value="
                        (final_datetime: string) => updateFinishDate(final_datetime)
                      "
                    />
                  </div>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions>
                  <v-btn
                    class="text-capitalize"
                    @click="isActive.value = false"
                  >
                    Fechar
                  </v-btn>

                  <v-spacer></v-spacer>

                  <v-btn
                    color="red-darken-3"
                    variant="tonal"
                    type="submit"
                    style="text-transform: none"
                  >
                    Sim, tenho certeza
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-form>
          </template>
        </v-dialog>
      </div>
    </div>

    <div class="pingo-table-content">
      <div class="v-elevation-z8 recentOrdersTable pingo-table">
        <v-data-table
          :headers="filteredColumns"
          :items="invoices"
          :loading="isLoadingFinancials"
          :items-per-page="pagination.totalPerPage"
          hide-default-footer
        >
          <!-- Templates para renderização de colunas -->
          <template v-slot:[`item.invoiceId`]="{ value }">
            <span class="fw-semibold main-color">{{ value }}</span>
          </template>

          <template v-slot:[`item.totalAmount`]="{ value }">
            {{
              new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(value)
            }}
          </template>

          <template v-slot:[`item.status`]="{ item }">
            <span
              :class="{
                'text-capitalize badge rounded-pill text-soft-danger':
                  item.status === 'cancelled',
                'text-capitalize badge rounded-pill text-soft-warning':
                  item.status === 'pending',
                'text-capitalize badge rounded-pill text-soft-info':
                  item.status === 'partially_paid',
                'text-capitalize badge rounded-pill text-soft-pingo':
                  item.status === 'refunded',
                'text-capitalize badge rounded-pill text-soft-success':
                  item.status === 'paid',
                'text-capitalize badge rounded-pill text-soft-primary':
                  item.status === 'overdue',
              }"
            >
              {{ mapStatusTitle(item.status as InvoiceStatus) }}
            </span>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex justify-content-center align-items-center ga-6">
              <v-dialog
                width="380"
                scrollable
                v-model="isInvoiceStatusDialogOpen"
              >
                <template v-slot:activator="{ props: activatorProps }">
                  <div
                    v-if="user.type === 'ADMIN' || user.type === 'ATTENDANT'"
                    class="d-flex ga-2"
                  >
                    <v-btn
                      v-if="item.status != InvoiceStatus.Paid"
                      class="text-capitalize"
                      color="blue-darken-3"
                      variant="tonal"
                      v-bind="activatorProps"
                      type="button"
                    >
                      Atualizar Status
                    </v-btn>
                  </div>
                </template>

                <template v-slot:default="">
                  <v-card class="py-0" title="Atualizar Status">
                    <v-divider></v-divider>

                    <v-card-text class="px-4">
                      <p class="mb-10">
                        Selecione o novo status para a fatura abaixo.
                        Certifique-se de que a alteração seja apropriada para o
                        estado atual da fatura.
                      </p>

                      <SelectInvoiceStatus
                        v-model="updateInvoiceForm.status"
                        :currentStatus="updateInvoiceForm.status as InvoiceStatus"
                        :allStatuses="allStatuses"
                        :error-messages="
                          v$.status.$errors
                            ? v$.status.$errors[0]?.$message
                            : undefined
                        "
                      />
                    </v-card-text>

                    <v-divider></v-divider>

                    <v-card-actions>
                      <v-btn
                        class="text-capitalize"
                        @click="
                          isInvoiceStatusDialogOpen = false;
                          updateInvoiceForm.status =
                            item.status as InvoiceStatus;
                        "
                      >
                        Fechar
                      </v-btn>

                      <v-spacer></v-spacer>

                      <v-btn
                        class="text-capitalize"
                        color="blue-darken-3"
                        variant="tonal"
                        @click="updateInvoiceStatus"
                      >
                        Atualizar
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </template>
              </v-dialog>

              <router-link :to="`/financeiro/${item.invoiceId}`">
                <v-btn
                  v-if="user.type === 'ADMIN' || user.type === 'BUSINESS'"
                  class="text-capitalize"
                  variant="tonal"
                  type="button"
                >
                  Ver detalhes
                </v-btn>
              </router-link>
            </div>
          </template>

          <template v-slot:no-data> Não há nenhuma fatura </template>

          <template v-slot:loading>
            <v-skeleton-loader type="table-row@10"></v-skeleton-loader>
          </template>
        </v-data-table>

        <ItemsPerPage
          class="mt-15"
          :total_pages="pagination.totalPages"
          :total_items="pagination.totalElements"
          @update:pagination="handleUpdatePageProps"
        />
      </div>
    </div>
  </v-card>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref, Ref, watch } from "vue";

import useVuelidate from "@vuelidate/core";

import { useAuthStore } from "@/stores/auth.store";
import { useFinancialStore } from "@/stores/financial.store";

import { useInvoice } from "@/composables/useInvoice";

import PageTitle from "@/components/Common/PageTitle.vue";

import FinancialFilters from "@/components/Financial/Filters/FinancialFilters.vue";

import ItemsPerPage from "@/components/Common/ItemsPerPage.vue";
import SelectInvoiceStatus from "@/components/Forms/AdvancedElements/Select/SelectInvoiceStatus.vue";
import AutoCompleteBusinesses from "@/components/Forms/AdvancedElements/AutoComplete/AutoCompleteBusinesses.vue";
import TextInput from "@/components/Forms/BasicElements/Input/TextInput.vue";

import { UserType } from "@/types/enum/userType.enum";
import { InvoiceStatus } from "@/types/enum/invoiceStatus.enum";

import type { UpdateInvoiceRequest } from "@/types/request/update/updateInvoice";
import type { InvoiceResponse } from "@/types/response/invoice";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { UserResponse } from "@/types/response/user";

export default defineComponent({
  name: "FinancialPage",
  components: {
    PageTitle,
    FinancialFilters,
    ItemsPerPage,
    SelectInvoiceStatus,
    AutoCompleteBusinesses,
    TextInput,
  },

  setup() {
    const authStore = useAuthStore();
    const financialStore = useFinancialStore();

    const {
      columns,
      pagination,
      invoice,
      invoices,
      processInvoicesMannually,
      getAllInvoices,
      createInvoiceFormRules,
      createInvoiceForm,
      createInvoice,
      updateInvoiceForm,
      updateInvoiceFormRules,
      updateInvoice,
    } = useInvoice();

    const user: Ref = ref(authStore.getUser);

    const isLoadingFinancials = ref(false);

    const isInvoiceStatusDialogOpen = ref(false);
    const isProcessInvoicesManualModalOpen = ref(false);
    const isProcessInvoicesIndividualModalOpen = ref(false);

    const allStatuses: InvoiceStatus[] = [
      InvoiceStatus.Pending,
      InvoiceStatus.Paid,
      InvoiceStatus.Overdue,
      InvoiceStatus.PartiallyPaid,
      InvoiceStatus.Cancelled,
      InvoiceStatus.Refunded,
    ];

    const filteredColumns = computed(() => {
      switch (user.value.type) {
        case UserType.BUSINESS:
        case UserType.COLLABORATOR:
          return columns.value.filter(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (column: any) => column.key !== "user.name"
          );

        default:
          return columns.value;
      }
    });

    const updateUserId = (value: UserResponse) =>
      (createInvoiceForm.userId = value.userId);

    const updateInitDate = (value: string) =>
      (createInvoiceForm.dateStart = value);

    const updateFinishDate = (value: string) =>
      (createInvoiceForm.dateEnd = value);

    const fetchFinancialsData = async () => {
      try {
        isLoadingFinancials.value = true;
        await getAllInvoices(
          pagination.value.currentPage,
          pagination.value.totalPerPage
        );
        isLoadingFinancials.value = false;
      } catch (error) {
        console.error("Erro ao buscar dados de faturas:", error);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleUpdatePageProps = async ({ page, size }: any) => {
      pagination.value.currentPage = page;
      pagination.value.totalPerPage = size;

      await fetchFinancialsData();
    };

    const mapInvoiceStatus = (
      item: InvoiceResponse,
      newStatus: InvoiceStatus
    ) => {
      invoice.value.invoiceId = item.invoiceId;
      invoice.value.status = item.status = newStatus;
    };

    const handleSubmit = async () => {
      const result = await v$2.value.$validate();

      if (!result) return;

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { ...newInvoicePayload } = createInvoiceForm;

      const response = await createInvoice(newInvoicePayload);

      if (response) {
        isProcessInvoicesIndividualModalOpen.value = false;
        await fetchFinancialsData();
      }
    };

    const updateInvoiceStatus = async () => {
      if (!invoice.value) return;

      const result = await v$.value.$validate();

      if (!result) return;

      const updatedInvoicePayload: UpdateInvoiceRequest = {
        status: updateInvoiceForm.status as InvoiceStatus,
      };

      const response = await updateInvoice(
        invoice.value.invoiceId,
        updatedInvoicePayload
      );

      if (response) {
        isInvoiceStatusDialogOpen.value = false;
        await fetchFinancialsData();
      }
    };

    const handleProcessInvoices = async () => {
      const response = await processInvoicesMannually();

      if (response) {
        isProcessInvoicesManualModalOpen.value = false;
        await fetchFinancialsData();
      }
    };

    const mapStatusTitle = (status: InvoiceStatus) => {
      switch (status) {
        case InvoiceStatus.Pending:
          return "Pendente";
        case InvoiceStatus.Paid:
          return "Pago";
        case InvoiceStatus.Overdue:
          return "Vencido";
        case InvoiceStatus.PartiallyPaid:
          return "Parcialmente Pago";
        case InvoiceStatus.Cancelled:
          return "Cancelado";
        case InvoiceStatus.Refunded:
          return "Reembolsado";
        default:
          return status;
      }
    };

    watch(
      () => financialStore.filters,
      (newFilters) => {
        isLoadingFinancials.value = true;

        getAllInvoices(
          pagination.value.currentPage,
          pagination.value.totalPerPage,
          newFilters.userId
        ).then(() => {
          isLoadingFinancials.value = false;
        });
      },
      { deep: true }
    );

    onMounted(async () => {
      await fetchFinancialsData();

      // Listen for the order-update event
      // EventBus.on("invoice-update", async () => {
      //   await fetchFinancialsData();
      // });
    });

    const v$ = useVuelidate(updateInvoiceFormRules, updateInvoiceForm);
    const v$2 = useVuelidate(createInvoiceFormRules, createInvoiceForm);

    return {
      UserType,
      InvoiceStatus,
      v$,
      v$2,
      user,
      invoices,
      pagination,
      isLoadingFinancials,
      isInvoiceStatusDialogOpen,
      isProcessInvoicesManualModalOpen,
      isProcessInvoicesIndividualModalOpen,
      allStatuses,
      filteredColumns,
      fetchFinancialsData,
      handleUpdatePageProps,
      mapStatusTitle,
      mapInvoiceStatus,
      updateInvoiceStatus,
      updateInvoiceForm,
      createInvoiceForm,
      handleProcessInvoices,
      handleSubmit,
      updateUserId,
      updateInitDate,
      updateFinishDate,
    };
  },
});
</script>
